import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import WysiwygEditor from "@/components/WysiwygEditor";
import { CMSPreview } from "./CMSPreview";
import { useTranslationStatus } from "@/hooks/useTranslationStatus";
import { TranslationStatusBadge } from "./TranslationStatusBadge";
import { Save, Plus, Trash2, Edit, Languages } from "lucide-react";

interface BecomeVendorEditorProps {
  user: any;
  onRefresh: () => void;
}

export const BecomeVendorEditor = ({ user, onRefresh }: BecomeVendorEditorProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [editorLanguage, setEditorLanguage] = useState<'fr' | 'en' | 'lg-fr' | 'sw'>('fr');
  const [isTranslating, setIsTranslating] = useState(false);
  
  const translationStatus = useTranslationStatus('cms_content', 'become-vendor-page', editorLanguage);

  useEffect(() => {
    fetchContent();
  }, [editorLanguage]);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('cms_content')
        .select('*')
        .eq('key', 'become-vendor-page')
        .eq('language', editorLanguage)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        const parsedContent = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
        setContent({ ...data, content: parsedContent });
      } else {
        // Pas de contenu pour cette langue, essayer de traduire depuis le français
        if (editorLanguage !== 'fr') {
          await translateFromFrench();
        } else {
          await createDefaultContent();
        }
      }
    } catch (error: any) {
      console.error('Error fetching content:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le contenu",
        variant: "destructive"
      });
    }
  };

  const createDefaultContent = async () => {
    const defaultContent = {
      hero: {
        badge: 'Plateforme #1 en RDC',
        title: 'Vendez sur Nazandoo et',
        titleHighlight: 'développez votre business',
        description: 'Rejoignez plus de 10 000 vendeurs qui font confiance à Nazandoo pour développer leur activité. Configuration en 24h, commission de seulement 8%, paiements garantis sous 24h.',
        ctaMain: 'Commencer à vendre maintenant',
        ctaSecondary: 'Voir une démonstration',
        demoLink: 'https://calendly.com'
      },
      stats: [
        { value: '500K+', label: 'Produits vendus' },
        { value: '98%', label: 'Vendeurs satisfaits' },
        { value: '15M$', label: "Chiffre d'affaires généré" },
        { value: '24h', label: 'Délai moyen de paiement' }
      ],
      benefits: [
        {
          title: "Revenus jusqu'à 100 000$ par mois",
          description: 'Nos top vendeurs génèrent des revenus mensuels exceptionnels',
          highlight: 'Commission de seulement 8%'
        },
        {
          title: '2M+ clients actifs',
          description: 'Accédez à notre base de clients grandissante en RDC',
          highlight: 'Croissance de 300% par an'
        },
        {
          title: "Expansion à travers l'Afrique",
          description: 'Votre vitrine sera bientôt disponible dans 15 pays',
          highlight: 'Lancement prévu 2024'
        },
        {
          title: 'Configuration en moins de 24h',
          description: 'Commencez à vendre dès demain avec notre processus simplifié',
          highlight: 'Validation rapide'
        }
      ],
      tools: [
        {
          title: 'Analytics Avancés',
          description: 'Tableaux de bord détaillés sur vos ventes, clients et performances'
        },
        {
          title: 'Publicité Ciblée',
          description: 'Boostez vos ventes avec nos outils de marketing intégrés'
        },
        {
          title: 'Logistique Simplifiée',
          description: 'Notre réseau de points relais gère la livraison pour vous'
        },
        {
          title: 'Support Dédié',
          description: 'Équipe d\'assistance disponible 7j/7 pour vous accompagner'
        }
      ],
      testimonials: [
        {
          name: 'Marie Kabila',
          business: 'Mode & Accessoires',
          revenue: '45 000$ /mois',
          quote: "En 6 mois sur Nazandoo, j'ai multiplié mes ventes par 10. La plateforme est intuitive et les clients font confiance.",
          rating: 5,
          image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=64&h=64&fit=crop&crop=face'
        },
        {
          name: 'Jean-Paul Mukendi',
          business: 'Électronique',
          revenue: '78 000$ /mois',
          quote: "Nazandoo m'a permis d'atteindre des clients dans toute la RDC. Les outils d'analyse sont exceptionnels.",
          rating: 5,
          image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face'
        },
        {
          name: 'Grace Mbuyi',
          business: 'Artisanat Local',
          revenue: '32 000$ /mois',
          quote: "Je vendais uniquement au marché central. Maintenant, mes créations touchent l'Afrique entière !",
          rating: 5,
          image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face'
        }
      ],
      sections: {
        benefits: {
          title: 'Pourquoi choisir Nazandoo ?',
          description: 'Des milliers de vendeurs nous font confiance pour développer leur business. Découvrez pourquoi Nazandoo est la plateforme de choix en RDC.'
        },
        tools: {
          title: 'Outils pour maximiser vos ventes',
          description: "Profitez d'une suite complète d'outils professionnels pour gérer et développer votre activité."
        },
        testimonials: {
          title: 'Success Stories',
          description: 'Découvrez comment nos vendeurs ont transformé leur business avec Nazandoo.'
        },
        form: {
          title: 'Rejoignez Nazandoo dès aujourd\'hui',
          description: 'Remplissez ce formulaire et commencez à vendre en moins de 24h'
        },
        process: {
          title: 'Comment ça marche ?',
          description: 'Processus simple et rapide pour commencer à vendre'
        },
        finalCta: {
          title: 'Prêt à transformer votre business ?',
          description: 'Rejoignez les milliers de vendeurs qui font confiance à Nazandoo',
          ctaMain: 'Commencer maintenant',
          ctaSecondary: 'Parler à un conseiller',
          phone: '+243123456789'
        }
      }
    };

    try {
      const { data, error } = await supabase
        .from('cms_content')
        .insert({
          key: 'become-vendor-page',
          title: 'Page Devenir Vendeur',
          content_type: 'page',
          status: 'published',
          language: editorLanguage,
          content: JSON.stringify(defaultContent),
          created_by: user?.id
        })
        .select()
        .single();

      if (error) throw error;
      setContent({ ...data, content: defaultContent });
      toast({ title: "Contenu par défaut créé avec succès" });
    } catch (error: any) {
      console.error('Error creating default content:', error);
    }
  };

  const translateFromFrench = async (forceRetranslate = false) => {
    try {
      setIsTranslating(true);
      
      // Récupérer le contenu en français
      const { data: frenchData, error: frenchError } = await supabase
        .from('cms_content')
        .select('*')
        .eq('key', 'become-vendor-page')
        .eq('language', 'fr')
        .maybeSingle();

      if (frenchError || !frenchData) {
        await createDefaultContent();
        return;
      }

      const frenchContent = typeof frenchData.content === 'string' 
        ? JSON.parse(frenchData.content) 
        : frenchData.content;

      toast({
        title: "Traduction en cours",
        description: `Traduction vers ${editorLanguage}...`,
      });

      const { data: translationData, error: translationError } = await supabase.functions.invoke(
        'translate-cms-content',
        {
          body: {
            content: frenchContent,
            sourceLanguage: 'fr',
            targetLanguage: editorLanguage
          }
        }
      );

      if (translationError) throw translationError;
      if (!translationData?.success) throw new Error('Translation failed');

      // Vérifier si le contenu existe déjà
      const { data: existingContent } = await supabase
        .from('cms_content')
        .select('id')
        .eq('key', 'become-vendor-page')
        .eq('language', editorLanguage)
        .maybeSingle();

      if (existingContent) {
        const { error: updateError } = await supabase
          .from('cms_content')
          .update({
            content: JSON.stringify(translationData.translatedContent),
            updated_at: new Date().toISOString()
          })
          .eq('key', 'become-vendor-page')
          .eq('language', editorLanguage);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('cms_content')
          .insert({
            key: 'become-vendor-page',
            title: `Page Devenir Vendeur (${editorLanguage})`,
            content_type: 'page',
            status: 'published',
            language: editorLanguage,
            content: JSON.stringify(translationData.translatedContent),
            created_by: user?.id
          });

        if (insertError) throw insertError;
      }

      setContent({ content: translationData.translatedContent, language: editorLanguage });
      toast({
        title: "Traduction réussie",
        description: "Le contenu a été traduit avec succès",
      });
      onRefresh();
    } catch (error: any) {
      console.error('Error translating content:', error);
      toast({
        title: "Erreur de traduction",
        description: error.message,
        variant: "destructive"
      });
      if (!forceRetranslate) {
        await createDefaultContent();
      }
    } finally {
      setIsTranslating(false);
    }
  };

  const handleRetranslate = async () => {
    if (editorLanguage === 'fr') {
      toast({
        title: "Erreur",
        description: 'Impossible de retraduire le contenu français',
        variant: "destructive"
      });
      return;
    }
    await translateFromFrench(true);
  };

  const handleSave = async () => {
    if (!content) return;
    
    setLoading(true);
    try {
      // Vérifier si le contenu existe pour cette langue
      const { data: existingContent } = await supabase
        .from('cms_content')
        .select('id')
        .eq('key', 'become-vendor-page')
        .eq('language', editorLanguage)
        .maybeSingle();

      if (existingContent) {
        // Mettre à jour le contenu existant
        const { error } = await supabase
          .from('cms_content')
          .update({
            content: JSON.stringify(content.content),
            updated_by: user?.id,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'become-vendor-page')
          .eq('language', editorLanguage);

        if (error) throw error;
      } else {
        // Créer un nouveau contenu pour cette langue
        const { error } = await supabase
          .from('cms_content')
          .insert({
            key: 'become-vendor-page',
            title: `Page Devenir Vendeur (${editorLanguage})`,
            content_type: 'page',
            status: 'published',
            language: editorLanguage,
            content: JSON.stringify(content.content),
            created_by: user?.id
          });

        if (error) throw error;
      }

      toast({ title: "Contenu sauvegardé avec succès" });
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateContent = (path: string[], value: any) => {
    setContent((prev: any) => {
      const newContent = { ...prev };
      let current = newContent.content;
      
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      
      current[path[path.length - 1]] = value;
      return newContent;
    });
  };

  const addArrayItem = (path: string[], item: any) => {
    setContent((prev: any) => {
      const newContent = { ...prev };
      let current = newContent.content;
      
      for (const key of path) {
        current = current[key];
      }
      
      current.push(item);
      return newContent;
    });
  };

  const removeArrayItem = (path: string[], index: number) => {
    setContent((prev: any) => {
      const newContent = { ...prev };
      let current = newContent.content;
      
      for (const key of path) {
        current = current[key];
      }
      
      current.splice(index, 1);
      return newContent;
    });
  };

  if (!content) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement du contenu...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Gérer la page "Devenir Vendeur"</CardTitle>
            <CardDescription>
              Modifiez tous les textes et contenus de la page
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Langue:</span>
              <select
                value={editorLanguage}
                onChange={(e) => setEditorLanguage(e.target.value as any)}
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="fr">🇫🇷 Français</option>
                <option value="en">🇬🇧 English</option>
                <option value="lg-fr">🇨🇩 Lingala</option>
                <option value="sw">🇹🇿 Swahili</option>
              </select>
            </div>
            {editorLanguage !== 'fr' && (
              <TranslationStatusBadge
                needsUpdate={translationStatus.needsUpdate}
                frenchTimestamp={translationStatus.frenchTimestamp}
                currentTimestamp={translationStatus.currentTimestamp}
                loading={translationStatus.loading}
              />
            )}
            <div className="flex gap-2">
              {editorLanguage !== 'fr' && (
                <Button 
                  variant="outline" 
                  onClick={handleRetranslate}
                  disabled={isTranslating || loading}
                >
                  <Languages className="h-4 w-4 mr-2" />
                  {isTranslating ? 'Traduction...' : 'Retraduire'}
                </Button>
              )}
              <Button onClick={handleSave} disabled={loading || isTranslating}>
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="hero">Hero</TabsTrigger>
            <TabsTrigger value="stats">Stats</TabsTrigger>
            <TabsTrigger value="benefits">Avantages</TabsTrigger>
            <TabsTrigger value="tools">Outils</TabsTrigger>
            <TabsTrigger value="testimonials">Témoignages</TabsTrigger>
            <TabsTrigger value="sections">Sections</TabsTrigger>
          </TabsList>

          {/* Hero Tab */}
          <TabsContent value="hero" className="space-y-4">
            <div className="grid gap-4">
              <div>
                <Label>Badge</Label>
                <Input
                  value={content.content.hero?.badge || ''}
                  onChange={(e) => updateContent(['hero', 'badge'], e.target.value)}
                />
              </div>
              <div>
                <Label>Titre</Label>
                <Input
                  value={content.content.hero?.title || ''}
                  onChange={(e) => updateContent(['hero', 'title'], e.target.value)}
                />
              </div>
              <div>
                <Label>Titre Highlight</Label>
                <Input
                  value={content.content.hero?.titleHighlight || ''}
                  onChange={(e) => updateContent(['hero', 'titleHighlight'], e.target.value)}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={content.content.hero?.description || ''}
                  onChange={(e) => updateContent(['hero', 'description'], e.target.value)}
                  rows={4}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>CTA Principal</Label>
                  <Input
                    value={content.content.hero?.ctaMain || ''}
                    onChange={(e) => updateContent(['hero', 'ctaMain'], e.target.value)}
                  />
                </div>
                <div>
                  <Label>CTA Secondaire</Label>
                  <Input
                    value={content.content.hero?.ctaSecondary || ''}
                    onChange={(e) => updateContent(['hero', 'ctaSecondary'], e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Lien Démo</Label>
                <Input
                  value={content.content.hero?.demoLink || ''}
                  onChange={(e) => updateContent(['hero', 'demoLink'], e.target.value)}
                  placeholder="https://calendly.com"
                />
              </div>
            </div>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats" className="space-y-4">
            {content.content.stats?.map((stat: any, index: number) => (
              <Card key={index}>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge>Stat {index + 1}</Badge>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => removeArrayItem(['stats'], index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Valeur</Label>
                      <Input
                        value={stat.value || ''}
                        onChange={(e) => {
                          const newStats = [...content.content.stats];
                          newStats[index].value = e.target.value;
                          updateContent(['stats'], newStats);
                        }}
                      />
                    </div>
                    <div>
                      <Label>Label</Label>
                      <Input
                        value={stat.label || ''}
                        onChange={(e) => {
                          const newStats = [...content.content.stats];
                          newStats[index].label = e.target.value;
                          updateContent(['stats'], newStats);
                        }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button
              onClick={() => addArrayItem(['stats'], { value: '', label: '' })}
              variant="outline"
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une statistique
            </Button>
          </TabsContent>

          {/* Benefits Tab */}
          <TabsContent value="benefits" className="space-y-4">
            {content.content.benefits?.map((benefit: any, index: number) => (
              <Card key={index}>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge>Avantage {index + 1}</Badge>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => removeArrayItem(['benefits'], index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    <Label>Titre</Label>
                    <Input
                      value={benefit.title || ''}
                      onChange={(e) => {
                        const newBenefits = [...content.content.benefits];
                        newBenefits[index].title = e.target.value;
                        updateContent(['benefits'], newBenefits);
                      }}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={benefit.description || ''}
                      onChange={(e) => {
                        const newBenefits = [...content.content.benefits];
                        newBenefits[index].description = e.target.value;
                        updateContent(['benefits'], newBenefits);
                      }}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Highlight</Label>
                    <Input
                      value={benefit.highlight || ''}
                      onChange={(e) => {
                        const newBenefits = [...content.content.benefits];
                        newBenefits[index].highlight = e.target.value;
                        updateContent(['benefits'], newBenefits);
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button
              onClick={() => addArrayItem(['benefits'], { title: '', description: '', highlight: '' })}
              variant="outline"
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un avantage
            </Button>
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools" className="space-y-4">
            {content.content.tools?.map((tool: any, index: number) => (
              <Card key={index}>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge>Outil {index + 1}</Badge>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => removeArrayItem(['tools'], index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    <Label>Titre</Label>
                    <Input
                      value={tool.title || ''}
                      onChange={(e) => {
                        const newTools = [...content.content.tools];
                        newTools[index].title = e.target.value;
                        updateContent(['tools'], newTools);
                      }}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={tool.description || ''}
                      onChange={(e) => {
                        const newTools = [...content.content.tools];
                        newTools[index].description = e.target.value;
                        updateContent(['tools'], newTools);
                      }}
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button
              onClick={() => addArrayItem(['tools'], { title: '', description: '' })}
              variant="outline"
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un outil
            </Button>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials" className="space-y-4">
            {content.content.testimonials?.map((testimonial: any, index: number) => (
              <Card key={index}>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge>Témoignage {index + 1}</Badge>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => removeArrayItem(['testimonials'], index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Nom</Label>
                      <Input
                        value={testimonial.name || ''}
                        onChange={(e) => {
                          const newTestimonials = [...content.content.testimonials];
                          newTestimonials[index].name = e.target.value;
                          updateContent(['testimonials'], newTestimonials);
                        }}
                      />
                    </div>
                    <div>
                      <Label>Business</Label>
                      <Input
                        value={testimonial.business || ''}
                        onChange={(e) => {
                          const newTestimonials = [...content.content.testimonials];
                          newTestimonials[index].business = e.target.value;
                          updateContent(['testimonials'], newTestimonials);
                        }}
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Revenus</Label>
                      <Input
                        value={testimonial.revenue || ''}
                        onChange={(e) => {
                          const newTestimonials = [...content.content.testimonials];
                          newTestimonials[index].revenue = e.target.value;
                          updateContent(['testimonials'], newTestimonials);
                        }}
                        placeholder="45 000$ /mois"
                      />
                    </div>
                    <div>
                      <Label>Note (1-5)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="5"
                        value={testimonial.rating || 5}
                        onChange={(e) => {
                          const newTestimonials = [...content.content.testimonials];
                          newTestimonials[index].rating = parseInt(e.target.value);
                          updateContent(['testimonials'], newTestimonials);
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Citation</Label>
                    <Textarea
                      value={testimonial.quote || ''}
                      onChange={(e) => {
                        const newTestimonials = [...content.content.testimonials];
                        newTestimonials[index].quote = e.target.value;
                        updateContent(['testimonials'], newTestimonials);
                      }}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>URL Image</Label>
                    <Input
                      value={testimonial.image || ''}
                      onChange={(e) => {
                        const newTestimonials = [...content.content.testimonials];
                        newTestimonials[index].image = e.target.value;
                        updateContent(['testimonials'], newTestimonials);
                      }}
                      placeholder="https://..."
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button
              onClick={() => addArrayItem(['testimonials'], {
                name: '',
                business: '',
                revenue: '',
                quote: '',
                rating: 5,
                image: ''
              })}
              variant="outline"
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un témoignage
            </Button>
          </TabsContent>

          {/* Sections Tab */}
          <TabsContent value="sections" className="space-y-6">
            {Object.entries(content.content.sections || {}).map(([key, section]: [string, any]) => (
              <Card key={key}>
                <CardHeader>
                  <CardTitle className="text-lg capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {section.title !== undefined && (
                    <div>
                      <Label>Titre</Label>
                      <Input
                        value={section.title || ''}
                        onChange={(e) => {
                          const newSections = { ...content.content.sections };
                          newSections[key].title = e.target.value;
                          updateContent(['sections'], newSections);
                        }}
                      />
                    </div>
                  )}
                  {section.description !== undefined && (
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={section.description || ''}
                        onChange={(e) => {
                          const newSections = { ...content.content.sections };
                          newSections[key].description = e.target.value;
                          updateContent(['sections'], newSections);
                        }}
                        rows={2}
                      />
                    </div>
                  )}
                  {section.ctaMain !== undefined && (
                    <div>
                      <Label>CTA Principal</Label>
                      <Input
                        value={section.ctaMain || ''}
                        onChange={(e) => {
                          const newSections = { ...content.content.sections };
                          newSections[key].ctaMain = e.target.value;
                          updateContent(['sections'], newSections);
                        }}
                      />
                    </div>
                  )}
                  {section.ctaSecondary !== undefined && (
                    <div>
                      <Label>CTA Secondaire</Label>
                      <Input
                        value={section.ctaSecondary || ''}
                        onChange={(e) => {
                          const newSections = { ...content.content.sections };
                          newSections[key].ctaSecondary = e.target.value;
                          updateContent(['sections'], newSections);
                        }}
                      />
                    </div>
                  )}
                  {section.phone !== undefined && (
                    <div>
                      <Label>Téléphone</Label>
                      <Input
                        value={section.phone || ''}
                        onChange={(e) => {
                          const newSections = { ...content.content.sections };
                          newSections[key].phone = e.target.value;
                          updateContent(['sections'], newSections);
                        }}
                        placeholder="+243123456789"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
